import { useEffect, useState, useCallback } from "react";
import { create, update, getCampaign } from "@form/services/campaign";
import useGlobalState from "./useGlobalState";
import { useRouter } from "next/dist/client/router";
import { getDefaultForm, Form } from "@form/components/form/modules/index";
import useAccount from "./useAccount";
import mixpanel from "mixpanel-browser";

import type { TitleForm } from "@form/components/form/modules/title";
import type { TwitterForm } from "@form/components/form/modules/twitter";
import type { WebsiteForm } from "@form/components/form/modules/website";
import type { DiscordForm } from "@form/components/form/modules/discord";

function extraFormTrackData(form: Form) {
  const data = {} as any;
  form.form.blocks.forEach((block) => {
    if (block.module.id === "title") {
      data.name = (block as TitleForm).values.title;
    } else if (block.module.id === "twitter") {
      data.twitter = (block as TwitterForm).values.accounts.join(",");
    } else if (block.module.id === "website") {
      data.website = (block as WebsiteForm).values.link;
    } else if (block.module.id === "discord") {
      data.discord = (block as DiscordForm).values.serverLinks.join(",");
    }
  });
  return data;
}

const useForm = (id?: string) => {
  const [form, setForm] = useGlobalState("activeForm");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { isLogin, login, address } = useAccount();

  const isOwner =
    (form && form.creator_address === address) || (form && !form.id);

  useEffect(() => {
    if (id) {
      if (form && form.id === id) {
        return;
      }
      if (id === "new" || id === "preview") {
        return;
      }
      setLoading(true);
      getCampaign(id)
        .then((form) => {
          setForm(form);
        })
        .catch((e: any) => {
          setError(e.message);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id, form]);

  useEffect(() => {
    if (id && id === "new") {
      if (form && !form.id) {
        return;
      }
      setForm(getDefaultForm());
    }
  }, [id, form]);

  const createForm = useCallback(
    async (f?: Form) => {
      const _form = f || form;
      if (!isLogin) {
        login();
        return false;
      }
      if (loading || !_form || _form.id) {
        return false;
      }
      try {
        setError("");
        setLoading(true);
        const newform = await create(_form);
        setForm(newform);
        mixpanel.track("publish_form", {
          form_id: newform.id,
          ...extraFormTrackData(_form),
        });
        setTimeout(() => {
          router.replace(
            {
              pathname:
                newform.config.type === "wallets"
                  ? "/dashboard/forms/[id]/response"
                  : "/dashboard/forms/[id]",
              query: {
                id: newform.id,
              },
            },
            undefined,
            {
              shallow: true,
            }
          );
        }, 50);
        return newform;
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [loading, form, isLogin, login]
  );

  const updateForm = useCallback(
    async (f?: Form) => {
      const _form = f || form;
      if (!isLogin) {
        login();
        return false;
      }
      if (loading || !_form || !_form.id) {
        return false;
      }
      try {
        setError("");
        setLoading(true);
        const updateForm = await update(_form.id, _form.form);
        mixpanel.track("update_form", {
          form_id: _form.id,
          ...extraFormTrackData(_form),
        });
        setForm(updateForm);
        setTimeout(() => {
          router.replace({
            pathname: "/dashboard/forms/[id]",
            query: {
              id: updateForm.id,
            },
          });
        }, 50);
        return updateForm;
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [loading, form, isLogin, login]
  );

  const publishForm = useCallback(
    async (newForm?: Form) => {
      const _form = newForm || form;
      if (!_form) {
        return;
      }
      if (!_form.id) {
        return await createForm(_form);
      } else {
        return await updateForm(_form);
      }
    },
    [form, createForm, updateForm]
  );

  return {
    form,
    isOwner,
    setForm,
    error,
    loading,
    createForm,
    updateForm,
    publishForm,
  };
};

export default useForm;
